import { faArrowRight, faCheck, faPlus, faRemove } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormik } from 'formik';
import * as React from 'react';
import { useEffect } from 'react';
import { useHistory } from 'react-router';
import LoadingScreen from '../../components/muiLegacy/LoadingScreen/LoadingScreen';
import VoxelButton from '../../components/voxel/Buttons/VoxelButton';
import VoxelIconButton from '../../components/voxel/Buttons/VoxelIconButton';
import VoxelCheckbox from '../../components/voxel/Forms/VoxelCheckbox';
import VoxelInput from '../../components/voxel/Forms/VoxelInput';
import VoxelSelect from '../../components/voxel/Forms/VoxelSelect';
import Logo from '../../components/voxel/Logo/Logo';
import VoxelText from '../../components/voxel/Text/VoxelText';
import VoxelDivider from '../../components/voxel/VoxelDivider';
import { CurrentUserGq } from '../../graphql/users/models/CurrentUserGq';
import { UserTeamRoleGq } from '../../graphql/users/models/UserTeamRoleGq';
import { usersApiService } from '../../graphql/users/usersApiService';

export interface RegisterTeamProps { }

export interface RegisterTeamMemberForm {
  members: { email: string; role: UserTeamRoleGq; disabled: boolean }[];
  allowedEmailDomain?: string;
  includeAllowedEmailDomain: boolean;
}

const RegisterTeamMembers: React.FC<RegisterTeamProps> = () => {
  const { user, query } = usersApiService.queries.useGetCurrentUser();
  const history = useHistory();

  // Form
  var memberRoleList = [
    {
      value: UserTeamRoleGq.MEMBER,
      title: 'Member',
      subtitle: 'Basic access',
    },
  ];
  memberRoleList.push({
    value: UserTeamRoleGq.ADMIN,
    title: 'Admin',
    subtitle: 'May edit basic settings, invite new users and set up team connections',
  });
  memberRoleList.push({
    value: UserTeamRoleGq.OWNER,
    title: 'Owner',
    subtitle: 'May edit subscription and billing in addition to admin rights',
  });

  const inviteMutation = usersApiService.mutations.useInviteUsersDuringRegistration();
  const updateMutation = usersApiService.mutations.useEditAllowedDomains();

  const getEmailDomain = (user?: CurrentUserGq) => {
    const domain = user?.email.split('@')[1]?.trim()?.toLowerCase().trim();
    const invalidDomains = ['gmail.com', 'outlook.com', 'yahoo.com', 'hotmail.com', 'aol.com'];
    return !domain?.length || invalidDomains.includes(domain) ? undefined : '@' + domain;
  };

  const formikProps = useFormik<RegisterTeamMemberForm>({
    enableReinitialize: true,
    initialValues: {
      allowedEmailDomain: getEmailDomain(user),
      includeAllowedEmailDomain: false,
      members: [
        { email: user?.email ?? '', role: user?.teamRole ?? UserTeamRoleGq.ADMIN, disabled: true },
        { email: '', role: UserTeamRoleGq.MEMBER, disabled: false },
        { email: '', role: UserTeamRoleGq.MEMBER, disabled: false },
      ],
    },
    onSubmit: (values) =>
      inviteMutation({
        users: values.members.map((m) => ({ email: m.email, role: m.role })),
      })
        .then(
          () =>
            values.includeAllowedEmailDomain &&
            updateMutation({
              teamId: user.team.id,
              allowedEmailDomains: [values.allowedEmailDomain],
            })
        )
        .finally(() => query.refetch()),
  });

  const addMemberDisabled = formikProps.values.members.length > 9;
  const addMember = () =>
    formikProps.setFieldValue('members', [
      ...formikProps.values.members,
      { email: '', role: UserTeamRoleGq.MEMBER, disabled: false },
    ]);

  // Don't show this page if setup has already been completed
  useEffect(() => {
    if (user?.team?.setupCompletedTeamMembers === true) {
      history.push('/register/success');
    }
  }, [user?.team]);

  if (!user?.team) return <LoadingScreen />;
  const emailRegex = new RegExp(/\w@\w/i);

  return (
    <div className={`w-full h-screen bg-white flex flex-row relative`}>
      <div className="w-auto h-auto absolute top-8 left-8">
        <Logo size="xs" />
      </div>
      <div className="mt-64 mx-auto w-[600px] flex flex-col items-center gap-6">
        <div className="">
          <VoxelText
            userSelectClass="select-none"
            className="whitespace-pre-line font-semibold"
            variant="title2"
            paddingBot
          >
            Invite team members
          </VoxelText>
          <VoxelText variant="bread-medium">
            Already know someone you would like to collaborate with? Get the most out of Voxel by
            adding team members.
          </VoxelText>
        </div>
        <div className="w-full flex flex-col items-start gap-3">
          <div className="w-full flex flex-row gap-3 items-end">
            <div className="grow">
              <VoxelText variant="form-caption">Email</VoxelText>
            </div>
            <div className="basis-1/3">
              <VoxelText variant="form-caption">Role</VoxelText>
            </div>
            <div className="grow-0">
              <div className={'opacity-0'}>
                <VoxelIconButton
                  icon={<FontAwesomeIcon icon={faRemove} />}
                  color="white"
                  disabled
                />
              </div>
            </div>
          </div>
          {formikProps.values.members.map((member, index) => (
            <div className="w-full flex flex-row gap-3 items-center">
              <div className="grow">
                {!member.disabled && !!member.email?.length && emailRegex.test(member.email) && (
                  <div className="absolute z-10 -left-9 top-3">
                    {' '}
                    <FontAwesomeIcon
                      icon={faCheck}
                      className="text-success-900 rounded-full bg-success-600/10 p-1"
                    />
                  </div>
                )}
                <VoxelInput
                  wrapperClassNames="w-full"
                  labelPosition="top"
                  placeholder="jane.doe@gmail.com"
                  value={member.email}
                  disabled={member.disabled}
                  onChange={(event) => {
                    formikProps.setFieldValue(
                      'members',
                      formikProps.values.members.map((newMember, newIndex) => {
                        if (newIndex !== index) return newMember;
                        return { ...newMember, email: event.target.value };
                      })
                    );
                    if (
                      !addMemberDisabled &&
                      index === formikProps.values.members.length - 1 &&
                      emailRegex.test(event.currentTarget.value)
                    ) {
                      addMember();
                    }
                  }}
                />
              </div>
              <div className="basis-1/3">
                <VoxelSelect
                  selectedOptionValue={member.role}
                  options={memberRoleList}
                  setSelectedOption={(value) =>
                    formikProps.setFieldValue(
                      'members',
                      formikProps.values.members.map((newMember, newIndex) => {
                        if (newIndex !== index) return newMember;
                        return { ...newMember, role: value.value };
                      })
                    )
                  }
                  disabled={member.disabled}
                />
              </div>
              <div className="grow-0">
                <div className={`${member.disabled && 'opacity-0'}`}>
                  <VoxelIconButton
                    icon={<FontAwesomeIcon icon={faRemove} />}
                    color="white"
                    disabled={member.disabled}
                    onClick={() =>
                      formikProps.setFieldValue(
                        'members',
                        formikProps.values.members.filter((m, i) => i !== index)
                      )
                    }
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="w-full flex flex-row ">
          <VoxelIconButton
            icon={<FontAwesomeIcon icon={faPlus} />}
            size="md"
            color="light-gray"
            disabled={addMemberDisabled}
            onClick={addMember}
          />
        </div>
        {!!formikProps.values.allowedEmailDomain?.length && (
          <>
            <VoxelDivider />
            <div className="w-[600px]">
              <VoxelCheckbox
                checked={formikProps.values.includeAllowedEmailDomain}
                color="primary"
                onChange={() =>
                  formikProps.setValues((prev) => ({
                    ...prev,
                    includeAllowedEmailDomain: !prev.includeAllowedEmailDomain,
                  }))
                }
                label={
                  <VoxelText span color="light">
                    Allow anyone with a{' '}
                    <span className="font-bold">{formikProps.values.allowedEmailDomain}</span> email
                    to join this team
                  </VoxelText>
                }
              />
            </div>
          </>
        )}
        <div />
        <div className="flex flex-row justify-center items-center gap-3 w-full pb-20">
          <div className="w-48">
            <VoxelButton
              title={formikProps.dirty ? 'Continue' : 'Skip'}
              color={formikProps.dirty ? 'primary' : 'gray'}
              fullWidth
              size="xl"
              startIcon={<FontAwesomeIcon icon={faArrowRight} className="opacity-0" />}
              endIcon={<FontAwesomeIcon icon={faArrowRight} />}
              onClick={formikProps.submitForm}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterTeamMembers;
